import { ErrorHandler, isDevMode } from '@angular/core';

const CHUNK_RELOAD_KEY = 'elite:chunk-error-reload';

/**
 * The app's global error handler. Its one duty is recovering from a stale chunk after a deploy.
 *
 * It used to also toast every error that reached it, which made it the third of four places that
 * decided what an Operator saw when a call failed — so a single failure could produce two toasts
 * (this one plus the effect's) or, when it took a path this handler never saw, none. Failure
 * reporting now belongs to `@elite/shared/util-http-response`, which is the only thing that toasts
 * an API failure, exactly once. Anything reaching here that is *not* a stale chunk is a programming
 * error rather than a call the Operator made; it is rethrown so Angular logs it with its stack.
 */
export class CustomErrorHandler implements ErrorHandler {
  handleError(error: unknown) {
    if (this.isChunkLoadError(error) && this.canReload()) {
      console.warn('[ErrorHandler] Stale chunk detected — reloading to fetch latest version.');
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
      return;
    }

    throw error;
  }

  /** Detects dynamic-import failures caused by stale chunk hashes after a deployment. */
  private isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return /loading chunk|ChunkLoadError|dynamically imported module/i.test(error.name + ' ' + error.message);
  }

  /** Prevents infinite reload loops — only auto-reload once per session. */
  private canReload(): boolean {
    // There is no chunk to re-fetch and no `sessionStorage` to record it in on the server.
    if (isDevMode() || typeof sessionStorage === 'undefined') return false;
    return !sessionStorage.getItem(CHUNK_RELOAD_KEY);
  }
}
